import React, { useMemo, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ResponsiveSunburst } from '@nivo/sunburst';
import './SwotMiniChart.css';

interface SubDimension {
  key: string;
  label: string;
  score: number | null;
  category: string | null;
  source?: string;
}

interface Dimension {
  key: string;
  label: string;
  score: number | null;
  category: string | null;
  sub_dimensions: SubDimension[];
}

interface Area {
  key: string;
  label: string;
  score: number | null;
  category: string | null;
  dimensions: Dimension[];
}

interface SwotMiniChartProps {
  areas: Area[];
  size?: number;
  onClick?: () => void;
}

// SWOT colors - matching main chart
const SWOT_THEME = {
  S: {
    primary: '#00FF88',
    secondary: '#33FF9F',
    tertiary: '#66FFB6',
    dark: '#00CC6D',
    glow: 'rgba(0, 255, 136, 0.3)',
    label: 'Strength',
  },
  O: {
    primary: '#7FFF00',
    secondary: '#99FF33',
    tertiary: '#B3FF66',
    dark: '#66CC00',
    glow: 'rgba(127, 255, 0, 0.3)',
    label: 'Improvements',
  },
  W: {
    primary: '#BA68C8',
    secondary: '#CE93D8',
    tertiary: '#E1BEE7',
    dark: '#9C27B0',
    glow: 'rgba(186, 104, 200, 0.3)',
    label: 'Weakness',
  },
  T: {
    primary: '#008B8B',
    secondary: '#00A2A2',
    tertiary: '#00B9B9',
    dark: '#006F6F',
    glow: 'rgba(0, 139, 139, 0.3)',
    label: 'Critical',
  },
};

// Get layer color based on depth
const getLayerColor = (category: string, depth: number, index: number): string => {
  const theme = SWOT_THEME[category as keyof typeof SWOT_THEME];
  if (!theme) return '#4b5563';
  
  if (depth === 1) return theme.primary;
  if (depth === 2) {
    const colors = [theme.dark, theme.primary, theme.secondary];
    return colors[index % colors.length];
  }
  const outerColors = [theme.secondary, theme.tertiary, theme.primary];
  return outerColors[index % outerColors.length];
};

interface NivoNode {
  id: string;
  name: string;
  color?: string;
  value?: number;
  children?: NivoNode[];
  category?: string;
  score?: number | null;
  source?: string;
  depth?: number;
  dimensionName?: string;
}

// Tooltip content component
const TooltipContent = ({ data }: { data: any }) => {
  const theme = data.category ? SWOT_THEME[data.category as keyof typeof SWOT_THEME] : null;
  
  const isCategory = data.depth === 1;
  const isDimension = data.depth === 2;
  const isSubDimension = data.depth === 3;
  
  return (
    <>
      {/* Category Badge */}
      {data.category && theme && (
        <div className="swot-mini-tooltip-badge" style={{ 
          backgroundColor: theme.glow,
          color: theme.primary,
        }}>
          {theme.label}
        </div>
      )}
      
      {/* Hierarchy Display */}
      <div className="swot-mini-tooltip-hierarchy">
        {/* Dimension Level */}
        {(isDimension || isSubDimension) && (
          <div className="swot-mini-tooltip-level">
            <div className="swot-mini-tooltip-header">
              <span className="swot-mini-tooltip-icon">▸</span>
              <span className="swot-mini-tooltip-label">Dimension</span>
            </div>
            <div className="swot-mini-tooltip-content">
              <span className="swot-mini-tooltip-name">
                {isDimension ? data.name : data.dimensionName}
              </span>
            </div>
          </div>
        )}
        
        {/* Sub-dimension Level */}
        {isSubDimension && (
          <div className="swot-mini-tooltip-level subdimension">
            <div className="swot-mini-tooltip-header">
              <span className="swot-mini-tooltip-icon">▸</span>
              <span className="swot-mini-tooltip-label">Sub-dimension</span>
            </div>
            <div className="swot-mini-tooltip-content">
              <span className="swot-mini-tooltip-name">{data.name}</span>
              {data.score !== undefined && data.score !== null && (
                <span className="swot-mini-tooltip-score" style={{ color: theme?.primary }}>
                  {data.score}
                </span>
              )}
            </div>
          </div>
        )}
        
        {/* Category Level Only */}
        {isCategory && (
          <div className="swot-mini-tooltip-level">
            <div className="swot-mini-tooltip-content">
              <span className="swot-mini-tooltip-name">{data.name}</span>
            </div>
          </div>
        )}
      </div>
      
      {/* Source */}
      {data.source && (
        <div className="swot-mini-tooltip-meta">
          <span className="swot-mini-tooltip-meta-label">Source:</span>
          <span className="swot-mini-tooltip-meta-value">{data.source}</span>
        </div>
      )}
    </>
  );
};

const SwotMiniChart: React.FC<SwotMiniChartProps> = ({
  areas,
  size = 220,
  onClick,
}) => {
  const [tooltipData, setTooltipData] = useState<any>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Track mouse position
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Transform data to full sunburst format (same as main chart)
  const chartData = useMemo((): NivoNode => {
    const swotGroups: Record<string, { dim: string; sub: SubDimension; dimIndex: number }[]> = {
      S: [], O: [], W: [], T: [],
    };

    areas.forEach(area => {
      area.dimensions.forEach((dim, dimIndex) => {
        dim.sub_dimensions.forEach(sub => {
          if (sub.score !== null && sub.score !== undefined && sub.category) {
            swotGroups[sub.category]?.push({ dim: dim.label, sub, dimIndex });
          }
        });
      });
    });

    const children: NivoNode[] = [];

    ['S', 'O', 'W', 'T'].forEach((cat) => {
      const items = swotGroups[cat];
      const theme = SWOT_THEME[cat as keyof typeof SWOT_THEME];
      
      if (items.length === 0) {
        children.push({
          id: cat,
          name: theme.label,
          color: theme.primary,
          value: 25,
          category: cat,
          depth: 1,
        });
        return;
      }

      // Group by dimension
      const byDimension: Record<string, { sub: SubDimension; dimIndex: number }[]> = {};
      items.forEach(item => {
        if (!byDimension[item.dim]) {
          byDimension[item.dim] = [];
        }
        byDimension[item.dim].push({ sub: item.sub, dimIndex: item.dimIndex });
      });

      const dimensionChildren: NivoNode[] = Object.entries(byDimension).map(([dimName, dimItems], dimIdx) => {
        const subChildren: NivoNode[] = dimItems.map((item) => ({
          id: `${cat}-${dimName}-${item.sub.key}`,
          name: item.sub.label,
          value: Math.max(item.sub.score || 1, 10),
          color: getLayerColor(cat, 3, dimIdx),
          category: cat,
          score: item.sub.score,
          source: item.sub.source,
          depth: 3,
          dimensionName: dimName,
        }));

        return {
          id: `${cat}-${dimName}`,
          name: dimName,
          color: getLayerColor(cat, 2, dimIdx),
          children: subChildren,
          category: cat,
          depth: 2,
        };
      });

      children.push({
        id: cat,
        name: theme.label,
        color: theme.primary,
        children: dimensionChildren,
        category: cat,
        depth: 1,
      });
    });

    return { id: 'root', name: 'SWOT', children };
  }, [areas]);

  // Stats
  const stats = useMemo(() => {
    let totalScore = 0;
    let count = 0;

    areas.forEach(area => {
      area.dimensions.forEach(dim => {
        dim.sub_dimensions.forEach(sub => {
          if (sub.score !== null && sub.category) {
            totalScore += sub.score;
            count++;
          }
        });
      });
    });

    return {
      average: count > 0 ? Math.round(totalScore / count) : 0,
      total: count,
    };
  }, [areas]);

  // Custom tooltip that captures data for portal rendering
  const CustomTooltip = ({ data }: any) => {
    // Update tooltip data when hovering
    useEffect(() => {
      setTooltipData(data);
      return () => setTooltipData(null);
    }, [data]);
    
    // Return null - we'll render via portal
    return null;
  };

  return (
    <div className="swot-mini-container" onClick={onClick}>
      <div className="swot-mini-chart" style={{ width: size, height: size }}>
        <ResponsiveSunburst
          data={chartData}
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          id="id"
          value="value"
          cornerRadius={2}
          borderWidth={2}
          borderColor="#1a1a1a"
          colors={(node: any) => node.data.color || '#4b5563'}
          inheritColorFromParent={false}
          childColor={{ from: 'color', modifiers: [['brighter', 0.2]] }}
          enableArcLabels={true}
          arcLabelsSkipAngle={25}
          arcLabelsTextColor="#ffffff"
          arcLabel={(node: any) => {
            if (node.depth === 1) {
              return node.id;
            }
            return '';
          }}
          tooltip={CustomTooltip}
          animate={true}
          motionConfig="gentle"
        />
        <div className="swot-mini-center">
          <span className="swot-mini-total">{stats.average}</span>
          <span className="swot-mini-label">AVG SCORE</span>
          <span className="swot-mini-metrics">{stats.total} metrics</span>
        </div>
      </div>
      
      {/* Portal tooltip - renders at body level, positioned to the right of cursor */}
      {tooltipData && createPortal(
        <div 
          className="swot-mini-tooltip-portal"
          style={{
            position: 'fixed',
            left: mousePos.x + 15,
            top: mousePos.y - 10,
            zIndex: 999999,
            pointerEvents: 'none',
          }}
        >
          <div className="swot-mini-tooltip">
            <TooltipContent data={tooltipData} />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SwotMiniChart;
